import {
  ArrowUturnDownIcon,
  CheckIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';
import { useState } from 'react';
import { trackEvent } from '@/app/analytics/track.ts';
import AccountConfirmModal from '@/app/components/account/account-confirm-modal.tsx';
import { useRejectAccountRepresentative } from '@/app/hooks/use-reject-account-representative.ts';
import { usePatchApproveAccountRepresentativeMutation } from '@/slices/account-representative-api-slice.ts';
import type { AccountRepresentativeListModel } from '../../types/account-representative.type.ts';
import { RepresentativeStatusEnum } from '../../types/representative-status.type.ts';
import { useEditAccountRepContext } from './representative-table.tsx';

const RepTableActions = ({ data }: { data: AccountRepresentativeListModel }) => {
  const [patchApprove] = usePatchApproveAccountRepresentativeMutation();
  const { rejectConnection, isRejecting } = useRejectAccountRepresentative();
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const { openEdit } = useEditAccountRepContext();

  const reject = async () => {
    try {
      await rejectConnection(data);
      setAlertIsOpen(false);
    } catch {
      // toast handled in hook
    }
  };

  const approve = async () => {
    try {
      await patchApprove(data.key).unwrap();
      trackEvent('connection_approved', {
        account_representative_key: data.key,
        account_key: data.accountKey,
        account_id: data.accountId,
        representative_key: data.representativeKey,
      });
    } catch {
      // mutation error handled by RTK
    }
  };

  const noStatus = !data.representativeStatus;
  const isRejected = data.representativeStatus?.id === RepresentativeStatusEnum.Rejected;
  const isApproved = data.representativeStatus?.id === RepresentativeStatusEnum.Approved;
  const canEditRep = !!data.representativeStatus?.id;
  const confirmTitle = isApproved ? 'Remove connection' : 'Reject representative';
  const confirmLabel = isApproved ? 'Remove connection' : 'Reject';
  const confirmEyebrow = isApproved ? 'Connection' : 'Access request';
  const confirmDescription = isApproved
    ? `Remove ${data.representativeFirstName} ${data.representativeLastName} from this account? They will not be able to request meetings here until they are connected again.`
    : `Reject ${data.representativeFirstName} ${data.representativeLastName}? This blocks the representative from this account.`;

  return (
    <div className='flex justify-end items-center gap-1'>
      {noStatus && (
        <>
          <Button onPress={approve} size='sm' variant='flat' color='secondary'>
            <CheckIcon className='size-5' />
            Approve
          </Button>
          <Button onPress={() => setAlertIsOpen(true)} size='sm' variant='flat' color='warning'>
            <XMarkIcon className='size-5' />
            Reject
          </Button>
          <Button
            isIconOnly
            onPress={() => openEdit(data)}
            size='sm'
            variant='flat'
            color='default'
          >
            <PencilIcon className='size-4' />
          </Button>
        </>
      )}

      {!noStatus && (
        <Dropdown placement='bottom-end'>
          <DropdownTrigger>
            <Button isIconOnly size='sm' variant='flat' color='default'>
              <EllipsisVerticalIcon className='size-5' />
            </Button>
          </DropdownTrigger>
          <DropdownMenu>
            {canEditRep && (
              <DropdownItem
                startContent={<PencilIcon className='size-5' />}
                key='edit'
                onPress={() => openEdit(data)}
              >
                Edit Representative
              </DropdownItem>
            )}
            {isRejected && (
              <DropdownItem
                key='approve'
                onPress={approve}
                startContent={<ArrowUturnDownIcon className='size-5' />}
              >
                Undo Reject & Approve
              </DropdownItem>
            )}
            {isApproved && (
              <DropdownItem
                key='reject'
                color='danger'
                className='text-danger'
                onPress={() => setAlertIsOpen(true)}
                startContent={<TrashIcon className='size-5' />}
              >
                Remove Representative
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      )}
      <AccountConfirmModal
        title={confirmTitle}
        eyebrow={confirmEyebrow}
        description={confirmDescription}
        confirmLabel={confirmLabel}
        isOpen={alertIsOpen}
        onClose={() => setAlertIsOpen(false)}
        onConfirm={reject}
        isLoading={isRejecting}
        tone='danger'
        icon={<TrashIcon className='size-5' />}
      />
    </div>
  );
};

export default RepTableActions;
